import type { LucideIcon } from 'lucide-react'
import { InteractiveDots, ScrollReveal, TextReveal } from '@/components/motion'
import { BRAND, halftone } from '@/components/landing/service/halftone'
import { TrialButton } from './shared'

/**
 * Copy of the shared blue ScrollCards band that also shows each benefit's
 * figure (the shared block has no stat slot): a sticky left column with the
 * heading, copy and trial button while white cards scroll up the right
 * column. One column in normal flow below lg.
 */
export function BenefitScrollCards({
  heading,
  headingId,
  body,
  cards,
}: {
  heading: React.ReactNode
  headingId: string
  body: string
  cards: Array<{ icon: LucideIcon; title: string; description: string; stat?: string }>
}) {
  return (
    <section aria-labelledby={headingId} className="relative overflow-x-clip bg-[var(--color-landing-accent)] text-white">
      <div aria-hidden className="pointer-events-none sticky top-0 -mb-[100svh] h-[100svh]">
        <InteractiveDots />
      </div>

      <div className="landing-container landing-section-lg relative z-10 grid gap-16 lg:grid-cols-[1.15fr_1fr] lg:gap-20">
        <div className="self-start lg:sticky lg:top-28">
          <TextReveal as="h2" className="landing-text-display-xl max-w-[18ch] text-white">
            <span id={headingId}>{heading}</span>
          </TextReveal>
          <ScrollReveal delay={0.15}>
            <p className="landing-text-body-lg mt-8 max-w-[52ch] text-pretty text-white/85">{body}</p>
          </ScrollReveal>
          <ScrollReveal delay={0.25} className="mt-10">
            <TrialButton surface="color" className="[&_a]:!border-[#ff00f2] [&_a]:!bg-[#ff00f2]" />
          </ScrollReveal>
        </div>

        <ul className="space-y-6 md:space-y-8 lg:pt-[60svh]">
          {cards.map((card, i) => {
            const Icon = card.icon
            return (
              <li key={card.title}>
                <ScrollReveal
                  distance={60}
                  className="flex min-h-[18rem] flex-col justify-between gap-12 bg-white p-8 text-[var(--color-landing-primary)] dark:bg-[#12121a] dark:text-white md:min-h-[22rem] md:p-10"
                >
                  <div className="flex items-start justify-between gap-6">
                    <span
                      aria-hidden
                      className="flex h-14 w-14 shrink-0 items-center justify-center text-white"
                      style={{ backgroundColor: i % 2 === 1 ? BRAND.pink : BRAND.blue, ...halftone(0.28, 5) }}
                    >
                      <Icon className="h-7 w-7" strokeWidth={1.75} />
                    </span>
                    {card.stat && (
                      <span className="text-right font-[family-name:var(--font-landing-display)] text-[clamp(1.75rem,3vw,2.75rem)] uppercase leading-[1.2] tracking-[-0.02em] text-[var(--color-landing-accent)] dark:text-[#8b85ff]">
                        {card.stat}
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-[family-name:var(--font-landing-display)] text-xl uppercase leading-[1.4] tracking-[-0.01em] md:text-2xl">
                      {card.title}
                    </h3>
                    <p className="landing-text-body mt-4 max-w-[40ch] text-pretty text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                      {card.description}
                    </p>
                  </div>
                </ScrollReveal>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}
